import { WithAnonymousUser } from '@components/WithUser/WithUser';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce from 'immer';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import Router, { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

// Client-only card preview — keeps the SSR HTML free of placeholder data and
// avoids the "Roy McAllister" example flashing before the cached card hydrates.
// The static shimmer fallback renders during SSR and while the chunk loads.
const CustomizeCardPreview = dynamic(
  () =>
    import('@components/CustomizeCardPreview/CustomizeCardPreview').then(
      (mod) => mod.CustomizeCardPreview,
    ),
  {
    ssr: false,
    loading: () => <CustomizeCardPreviewShimmer />,
  },
);

// Mobile-only and only for referred users, so it stays out of the initial bundle.
const CardPreviewModal = dynamic(
  () => import('@components/CustomizeCard/CardPreviewModal').then((mod) => mod.CardPreviewModal),
  { ssr: false },
);

import { getCardsByUser } from '@api/card-api';
import AddFieldSection from '@components/AddFieldSections/AddCardFieldSection';
import CardImagesSubsection from '@components/Card/CardHeader/CardHeaderPictures/CardPicturesSubsection';
import ThemeSwatchRow from '@components/ColorSelector/ThemeSwatchRow';
import { LayoutPicker } from '@components/CustomizeCard/LayoutPicker';
import { NameFields } from '@components/CustomizeCard/NameFields';
import { CustomizeCardPreviewShimmer } from '@components/CustomizeCardPreview/CustomizeCardPreview';
import { ExistingUserHandler } from '@components/Onboarding/existing-user-handler';
import OnboardingLayout, {
  CustomizeCardForwardButton,
  LegalFooterDisclaimer,
  MainSection,
  MainSectionContents,
  MainSectionHeader,
  NavigationActions,
  NavigationButtonsSection,
  SideSection,
} from '@components/OnboardingLayout/CustomizeCardLayout';
import { mobileOnly } from '@components/OnboardingLayout/CustomizeCardLayout.css';
import { cardContext } from '@context/card-context';
import { CardEditPopoverShellContext } from '@context/card-edit-popover-shell-context';
import { cardFieldEditContext } from '@context/card-field-edit-context';
import CardHeaderEditProvider from '@context/card-header-edit/provider';
import { upgradeFlowContext } from '@context/upgrade-flow-context';
import { useUser } from '@context/user-context';
import { useEditBlinqCard } from '@hooks/card/use-edit-card';
import { useCelloReferralCompletion } from '@hooks/cello/use-cello-referral-completion';
import useAnonymousUser from '@hooks/use-anonymous-user';
import useCardFieldEditContext from '@hooks/use-card-field-edit-context';
import { useCreateUserDoc } from '@hooks/use-create-user-doc';
import useNode from '@hooks/use-node';
import useOnce from '@hooks/use-once';
import useTrigger from '@hooks/use-trigger';
import { useUpdateUserDoc } from '@hooks/use-update-user-doc';
import useVersioning from '@hooks/use-versioning';
import { analytics } from '@modules/analytics';
import { isIdentityBelongToAnonUser } from '@modules/auth-helpers';
import {
  DEFAULT_CARD_COLOR,
  createExampleCard,
  createExampleCardFromCache,
  extractCardFields,
  extractCardMetadata,
  generateFieldID,
} from '@modules/card-helpers';
import { type FirebaseUser, auth } from '@modules/firebase';
import { logger } from '@modules/logger';
import {
  readCachedOnboardingCard,
  writeCachedOnboardingCard,
} from '@modules/onboarding-card-cache';
import { getCachedPrefilledCardData } from '@modules/referral-helpers';
import { createAnonymousUser, logout } from '@modules/user-helpers';
import { cardFieldsReducer } from '@reducers/card-reducers';
import { Button } from '@ui-lib';
import ReactDOM from 'react-dom';
import type {
  AnonymousUser,
  Card,
  CardFields,
  CardLayout,
  CardMetadata,
  CardUrl,
  FieldTypes,
  User,
} from 'types';

import styles from './customize-card.module.scss';

// Re-used components
const CardEditSection: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['card-edit-section']}>{children}</div>;
};
const CardEditSectionHeaderRow: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['card-edit-section-header-row']}>{children}</div>;
};
const CardEditSectionHeader: React.FC<React.PropsWithChildren<{ text: string }>> = ({ text }) => {
  return <h3 className={styles['card-edit-section-header']}>{text}</h3>;
};

const CustomiseCardPage: NextPage = () => {
  const { user, identity, loading: userIsLoading } = useUser();
  const { updateUserDoc } = useUpdateUserDoc();
  const { editBlinqCard } = useEditBlinqCard();
  const { routeToReferralPaywall } = useCelloReferralCompletion();
  const anonymousUser = useAnonymousUser();
  const onboardingCacheOwnerId = user?.userId ?? anonymousUser?.uid;
  const { query, isReady: urlQueryIsReady } = useRouter();
  const [cardFieldEditContextProps] = useCardFieldEditContext();
  const [showCardPreview, setShowCardPreview] = useState(false);

  // Seed an example card so the preview renders on first paint instead of
  // waiting for Firebase auth to resolve. The example card is intentionally
  // identical between server and client to avoid hydration mismatches; the
  // localStorage cache rehydrates in a post-mount effect just below.
  const initialCardData = useMemo(() => ({ card: createExampleCard({}), prefilled: false }), []);

  const [cardMetadata, setCardMetadata] = useState<CardMetadata | undefined>(() =>
    extractCardMetadata(initialCardData.card),
  );
  const [cardFields, cardFieldsDispatch] = useReducer(cardFieldsReducer, undefined, () =>
    extractCardFields(initialCardData.card),
  );
  const loading = false;
  const [saving, setSaving] = useState(false);
  const cardFieldVersioner = useVersioning<CardFields>(cardFields);
  const cardPrefilled = useRef(initialCardData.prefilled);
  const [cardNode, cardRef] = useNode();
  const cardHeadingNode = useNode<HTMLHeadingElement>();

  const newFieldTrigger = useTrigger<string>((fieldId) => {
    const cardFieldEl = cardNode?.querySelector(`[data-id="${fieldId}"]`) as
      | HTMLButtonElement
      | undefined;

    if (!cardFieldEl) return;

    cardFieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  const [showExistingUserHandlerScreen, setShowExistingUserHandlerScreen] = useState(false);
  // We store this in state because we don't want it to be affected if the user selects to create a new account. If we
  // directly used identity.email in the UI then when they are logged out the email would go blank.
  const [existingUserEmailAddress, setExistingUserEmailAddress] = useState('');

  const [onboardingCard, setOnboardingCard] = useState<Card>(initialCardData.card);
  const [startedEditing, setStartedEditing] = useState(initialCardData.prefilled);
  const { createUserDoc } = useCreateUserDoc();

  useEffect(() => {
    Router.prefetch('/login');
    Router.prefetch('/onboarding/signup');
  }, []);

  // Rehydrate from the last-known card snapshot (or referral cache) right
  // after mount. The preview region is loaded via next/dynamic with ssr:false
  // and a shimmer fallback, so the chunk doesn't finish loading until after
  // this effect has run — the preview's first render uses the cached state
  // and no placeholder is ever painted.
  useOnce(
    (preventSecondRun) => {
      if (typeof window === 'undefined' || !onboardingCacheOwnerId) {
        return;
      }
      preventSecondRun();
      const lastKnown = readCachedOnboardingCard(onboardingCacheOwnerId);
      if (lastKnown) {
        const merged: Card = { ...initialCardData.card, ...lastKnown };
        setCardFieldsAndMetadata(merged);
        setOnboardingCard(merged);
        cardPrefilled.current = true;
        setStartedEditing(true);
        return;
      }
      const referral = getCachedPrefilledCardData();
      if (referral) {
        const fromReferral = createExampleCardFromCache(referral);
        if (fromReferral) {
          setCardFieldsAndMetadata(fromReferral);
          setOnboardingCard(fromReferral);
          cardPrefilled.current = true;
          setStartedEditing(true);
        }
      }
    },
    [onboardingCacheOwnerId],
  );

  const handlePrefillCardForExistingUser = async (user: User | undefined) => {
    if (!user) {
      return;
    }
    const cards = await getCardsByUser(user.userId);

    if (!cards[0]) {
      return;
    }

    cardPrefilled.current = true;
    return cards[0];
  };

  const handlePrefillCardForFirebaseUser = (
    firebaseUser: FirebaseUser | undefined,
    prefilledDataFromCache: Record<string, string> | null, // This is the data that was stored in the cache when the user was referred
  ) => {
    // If the displayName is present, split on the first instance of space
    const displayName = firebaseUser?.displayName?.split(/\s(.*)/);
    const email = !firebaseUser?.email?.includes('@privaterelay.appleid.com')
      ? firebaseUser?.email
      : undefined;

    const prefilledCard = createExampleCardFromCache(prefilledDataFromCache, {
      firstName: displayName?.[0],
      lastName: displayName?.[1],
      email: email ?? undefined,
    });

    if (!prefilledCard) {
      return;
    }

    if (
      prefilledCard.firstName ||
      prefilledCard.lastName ||
      prefilledCard.emails?.length ||
      prefilledCard.jobTitle ||
      prefilledCard.phoneNumbers?.length ||
      prefilledCard.orgName
    ) {
      // only set to current if some values were added
      cardPrefilled.current = true;
    }

    return prefilledCard;
  };

  useOnce(
    (preventSecondRun) => {
      if (userIsLoading || !urlQueryIsReady) return;

      async function initialise(user: User | undefined, firebaseUser: FirebaseUser | undefined) {
        if (firebaseUser && user?.isAnonymous === false && query.source !== 'onboarding') {
          if (query.logout === 'true') {
            // Logs user out if they are logged in and the query param is present
            await logout();
            location.reload();
            return;
          }
          setExistingUserEmailAddress(user?.signInEmailAddress || '');
          setShowExistingUserHandlerScreen(true);
          return;
        }

        if (window.location.search.includes('source=onboarding') === false) {
          const url = new URL(window.location.href);
          url.searchParams.set('source', 'onboarding');

          Router.replace(url, url, { shallow: true });
        }

        ReactDOM.unstable_batchedUpdates(async () => {
          // Initial card was seeded synchronously above. Only overwrite it if a
          // richer source (existing user's Firestore card, or Firebase Auth
          // profile) is available.
          let card: Card | undefined;
          const prefilledDataFromCache = getCachedPrefilledCardData();

          if (user) {
            card = await handlePrefillCardForExistingUser(user);
          } else if (!isIdentityBelongToAnonUser(firebaseUser)) {
            card = handlePrefillCardForFirebaseUser(firebaseUser, prefilledDataFromCache);
          }

          if (card) {
            setCardFieldsAndMetadata(card);
            setOnboardingCard(card);
            if (onboardingCacheOwnerId) {
              writeCachedOnboardingCard(onboardingCacheOwnerId, card);
            }
          }

          if (cardPrefilled.current) {
            setStartedEditing(true);
          }
        });
      }
      preventSecondRun();
      initialise(user, identity);
    },
    [user, userIsLoading, identity],
  );

  /**
   * Where this screen sends the user once their card is saved.
   *
   * An anonymous identity still has to create an account, so it goes on to signup and
   * finishes there. An identity that is already authenticated finishes *here* — this is
   * the tail of the login page's new-account detour, which reaches customize-card
   * without ever passing through SignupStep. A referred user is offered their discount
   * before the welcome destination, because this is the only place left to offer it
   * (ACT-2224).
   */
  const handleForward = useCallback(async () => {
    // Permanently suppress the new-user upgrade auto-prompt for anyone who came
    // through this onboarding flow. The upgrade modal can still be opened manually by
    // clicking "Upgrade" — only the auto-open is gated.
    localStorage.setItem('hasCompletedOnboarding', 'true');

    if (isIdentityBelongToAnonUser(auth.currentUser)) {
      const emailHintQueryParam = query.login_hint
        ? `&login_hint=${encodeURIComponent(String(query.login_hint))}`
        : '';
      void Router.push(`/onboarding/signup?source=onboarding${emailHintQueryParam}`);
      return;
    }

    if (!user) {
      return;
    }

    if (await routeToReferralPaywall(user.userId)) {
      return;
    }

    void Router.push(`/${user.userId}/cards/personal?welcomeApp=1`);
  }, [query.login_hint, routeToReferralPaywall, user]);

  const handleSave = useCallback(
    async (
      onComplete: (user: User | AnonymousUser, card: Card, mode: 'create' | 'update') => void,
    ) => {
      if (!onboardingCard) {
        return;
      }

      setSaving(true);

      // Saved from the live field state rather than `onboardingCard`, which only
      // catches up with edits once a popover saves. Without this, a name typed
      // and then followed straight by "Next" was never persisted.
      const editedCard: Card = { ...onboardingCard, ...cardFields, ...cardMetadata };

      if (anonymousUser?.uid && !user?.userId) {
        const newUser: AnonymousUser = createAnonymousUser(anonymousUser, {
          firstName: editedCard.firstName,
          lastName: editedCard.lastName,
          userId: anonymousUser.uid,
          isAnonymous: anonymousUser.isAnonymous,
          cardIds: [editedCard.cardId],
        });

        const card = {
          ...editedCard,
          userId: anonymousUser.uid,
        };

        await createUserDoc(newUser);
        await editBlinqCard({ card: card, user: newUser });
        writeCachedOnboardingCard(newUser.userId, card);

        onComplete?.(newUser, card, 'create');
      } else if (user) {
        // The user already has user details and a card, i.e. they have completed this step but revisited it.
        const updatedUser = {
          ...user,
          firstName: editedCard.firstName,
          lastName: editedCard.lastName,
        };

        const card = { ...editedCard, userId: user.userId };

        try {
          await updateUserDoc(updatedUser);
          await editBlinqCard({ card: card, user: updatedUser });
          writeCachedOnboardingCard(updatedUser.userId, card);

          onComplete?.(updatedUser, card, 'update');
        } catch (error) {
          logger.error('Error Saving Card', { error });
        }
      }
    },
    [
      onboardingCard,
      anonymousUser,
      user,
      editBlinqCard,
      cardFields,
      cardMetadata,
      createUserDoc,
      updateUserDoc,
    ],
  );

  const handleSaveFieldChanges = useCallback(
    (immediate?: boolean) => {
      cardFieldVersioner.saveChanges({ immediate });
      setOnboardingCard((currentOnboardingCard) => ({
        ...currentOnboardingCard,
        ...cardFields,
        ...cardMetadata,
      }));
    },
    [cardFieldVersioner, cardFields, cardMetadata],
  );

  function handleRollbackFieldChanges() {
    cardFieldVersioner.rollback((cardFields) => {
      cardFieldsDispatch({ type: 'UPDATE_ALL', value: cardFields });
    });
  }

  // if user changes card colour LAST before clicking 'Next' button, it
  // won't persist. This useEffect saves that colour change to the card.
  //
  // `handleSaveFieldChanges` is intentionally NOT in the dep array: its
  // identity changes on every `cardFields` edit, and including it here
  // re-bookmarks the versioner on every keystroke / image upload. That
  // breaks popover rollback — pressing Cancel after an upload would
  // "roll back" to the state with the upload already applied.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    handleSaveFieldChanges();
  }, [cardMetadata]);

  function handleQueueActionOnSave() {
    // Do nothing
  }

  function handleColorSelect(color: string) {
    if (!cardMetadata) return;

    const newState = produce(cardMetadata, (draftCardData) => {
      draftCardData.color = color;
    });
    setCardMetadata(newState);
  }

  const [waitForAnimationTimeOut, setWaitForAnimationTimeout] = useState(true);
  const EXAMPLE_CARD_ANIMATION_DURATION_MS = 200;
  const USER_CARD_ANIMATION_DURATION_MS = 300;

  function handleAddField(type: FieldTypes, subType: CardUrl['type'] | undefined) {
    if (!startedEditing) {
      setStartedEditing(true);
    }

    let id = generateFieldID();

    switch (type) {
      case 'name':
        cardFieldsDispatch({ type: 'ADD_NAME' });
        id = 'name';
        break;
      case 'job_title':
        cardFieldsDispatch({ type: 'ADD_JOB_TITLE' });
        id = 'job_title';
        break;
      case 'org_name':
        cardFieldsDispatch({ type: 'ADD_COMPANY' });
        id = 'org_name';
        break;
      case 'department':
        cardFieldsDispatch({ type: 'ADD_DEPARTMENT' });
        id = 'department';
        // Not bothering with email signature since we are deprecating it shortly anyway
        break;
      case 'accreditations':
        cardFieldsDispatch({ type: 'ADD_ACCREDITATION' });
        id = 'accreditations';
        break;
      case 'headline':
        cardFieldsDispatch({ type: 'ADD_HEADLINE' });
        id = 'headline';
        break;
      case 'phone':
        cardFieldsDispatch({ type: 'ADD_PHONE_NUMBER', id });
        break;
      case 'email':
        cardFieldsDispatch({ type: 'ADD_EMAIL', id });
        break;
      case 'address':
        cardFieldsDispatch({ type: 'ADD_ADDRESS', id });
        break;
      case 'url':
        if (!subType) throw new TypeError('Sub Type not provided for URL field');
        cardFieldsDispatch({ type: 'ADD_URL', urlType: subType, id });
        break;
    }

    activateFieldOncePreviewSettles(() => {
      cardFieldEditContextProps.requestToMakeActive(id, 'create');
      newFieldTrigger(id);
    });
  }

  /**
   * Opens a field editor on the card preview. The first edit swaps the example
   * card for the user's card, and the editor can only anchor to a field once
   * that swap has animated in, so the very first activation is delayed by the
   * animation.
   */
  function activateFieldOncePreviewSettles(makeFieldActive: () => void) {
    // We only want this to happen once on the initial change from static card to user edited card
    if (waitForAnimationTimeOut) {
      // 50 ms buffer
      const animationTimeout =
        EXAMPLE_CARD_ANIMATION_DURATION_MS + USER_CARD_ANIMATION_DURATION_MS + 50;
      setTimeout(() => {
        setWaitForAnimationTimeout(false);
        makeFieldActive();
      }, animationTimeout);
    } else {
      makeFieldActive();
    }
  }

  function handleNameChange(part: 'first' | 'last', value: string) {
    if (!startedEditing) {
      setStartedEditing(true);
    }

    cardFieldsDispatch({
      type: part === 'first' ? 'UPDATE_FIRST_NAME' : 'UPDATE_LAST_NAME',
      value,
    });
    // Bookmarked straight away so a later popover's Cancel rolls back to the
    // typed name rather than wiping it.
    cardFieldVersioner.saveChanges();
  }

  function handleOpenNameEditor() {
    if (!startedEditing) {
      setStartedEditing(true);
    }

    activateFieldOncePreviewSettles(() => {
      cardFieldEditContextProps.requestToMakeActive('name', 'update');
    });
  }

  function handleLayoutSelect(layout: CardLayout) {
    if (!startedEditing) {
      setStartedEditing(true);
    }

    cardFieldsDispatch({ type: 'SET_LAYOUT', value: layout });
    cardFieldVersioner.saveChanges();
    analytics.track({ name: 'changelayout_confirmed', properties: { module: 'card_designer' } });
  }

  function notReachable() {
    // Not reachable
  }

  const setCardFieldsAndMetadata = (card: Card) => {
    const cardMetadata = extractCardMetadata(card);
    const newCardFields = extractCardFields(card);
    setCardMetadata(cardMetadata);
    cardFieldsDispatch({ type: 'UPDATE_ALL', value: newCardFields });
    cardFieldVersioner.saveChanges();
  };

  // The gate is exactly what the helper text under the first-name input says.
  const isNextDisabled = !cardFields.firstName?.trim();

  return (
    <OnboardingLayout title="Customize card">
      <div data-testid="customize-card-page" />
      <upgradeFlowContext.Provider value={{ flowType: 'onboarding' }}>
        <cardContext.Provider
          value={{
            allFieldTypesLocked: false,
            cardId: cardMetadata?.cardId ?? '',
            canLock: false,
            cardColor: cardMetadata?.color || DEFAULT_CARD_COLOR,
            cardFields,
            cardFieldsDispatch,
            colorLocked: false,
            layoutImagesLocked: false,
            lockedFields: [],
            lockedFieldTypes: [],
            qrCodeLocked: false,
            userId: cardMetadata?.userId ?? '',
            orgId: cardMetadata?.orgId,
            queueActionOnSave: handleQueueActionOnSave,
            rollbackChanges: handleRollbackFieldChanges,
            saveChanges: handleSaveFieldChanges,
            toggleColorLock: notReachable,
            toggleFieldLock: notReachable,
            toggleFieldTypeLock: notReachable,
            toggleLayoutImagesLocked: notReachable,
            toggleLockAllFieldTypes: notReachable,
            toggleQRCodeLock: notReachable,
          }}
        >
          {showExistingUserHandlerScreen && (
            <ExistingUserHandler
              existingUserEmailAddress={existingUserEmailAddress}
            ></ExistingUserHandler>
          )}

          {!showExistingUserHandlerScreen && (
            <CardEditPopoverShellContext.Provider value={{ mode: 'dialog' }}>
              <cardFieldEditContext.Provider value={cardFieldEditContextProps}>
                <CardHeaderEditProvider>
                  <SideSection theme="light">
                    <div className={styles['side-section-content']} data-animate-layout-change>
                      <CustomizeCardPreview
                        startedEditing={startedEditing}
                        loading={loading}
                        cardHeadingNode={cardHeadingNode}
                        cardRef={cardRef}
                      />
                    </div>
                  </SideSection>
                  <MainSection>
                    <MainSectionContents>
                      <MainSectionHeader
                        title="Create your first card"
                        explainer="Ready to design your card. Start by adding your name."
                      />

                      <CardEditSection>
                        <NameFields
                          firstName={cardFields.firstName ?? ''}
                          lastName={cardFields.lastName ?? ''}
                          onFirstNameChange={(value) => handleNameChange('first', value)}
                          onLastNameChange={(value) => handleNameChange('last', value)}
                          onMoreFields={handleOpenNameEditor}
                        />
                      </CardEditSection>

                      <CardEditSection>
                        <CardEditSectionHeaderRow>
                          <CardEditSectionHeader text="Add images" />
                        </CardEditSectionHeaderRow>

                        <div className={styles['card-images']}>
                          <CardImagesSubsection
                            popoverNode={cardHeadingNode[0]}
                            {...cardFields}
                            isPreset={false}
                            onUserStartedAddingImages={() => setStartedEditing(true)}
                          />
                        </div>
                      </CardEditSection>

                      <CardEditSection>
                        <CardEditSectionHeaderRow>
                          <CardEditSectionHeader text="Brand color" />
                        </CardEditSectionHeaderRow>
                        <ThemeSwatchRow color={cardMetadata?.color} onSelect={handleColorSelect} />
                      </CardEditSection>

                      <CardEditSection>
                        <div className={styles['card-edit-section-header-row']} data-tight>
                          <CardEditSectionHeader text="Layout" />
                        </div>
                        <p className={styles['card-edit-section-explainer']}>
                          Add images to unlock more layouts.
                        </p>
                        <LayoutPicker
                          cardColor={cardMetadata?.color}
                          companyLogoImg={cardFields.companyLogoImg}
                          coverPhotoImg={cardFields.coverPhotoImg}
                          profilePictureImg={cardFields.profilePictureImg}
                          currentLayout={cardFields.layout}
                          onSelect={handleLayoutSelect}
                        />
                      </CardEditSection>

                      <CardEditSection>
                        <CardEditSectionHeaderRow>
                          <CardEditSectionHeader text="Add details" />
                        </CardEditSectionHeaderRow>

                        <AddFieldSection
                          fields={cardFields}
                          onAdd={handleAddField}
                          tileVariant="compact"
                          hiddenFieldTypes={['name']}
                        />
                      </CardEditSection>
                      <NavigationButtonsSection stackOnMobile>
                        <LegalFooterDisclaimer />
                        <NavigationActions>
                          <div className={mobileOnly}>
                            <Button
                              variant="outline"
                              size="regular"
                              width="full"
                              onClick={() => setShowCardPreview(true)}
                            >
                              Preview
                            </Button>
                          </div>
                          <CustomizeCardForwardButton
                            onClick={() => handleSave(handleForward)}
                            loading={saving}
                            disabled={isNextDisabled}
                            disabledReason="Add your first name to continue"
                          >
                            Next
                            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
                          </CustomizeCardForwardButton>
                        </NavigationActions>
                      </NavigationButtonsSection>
                    </MainSectionContents>
                  </MainSection>
                  <CardPreviewModal
                    opened={showCardPreview}
                    onClose={() => setShowCardPreview(false)}
                  />
                </CardHeaderEditProvider>
              </cardFieldEditContext.Provider>
            </CardEditPopoverShellContext.Provider>
          )}
        </cardContext.Provider>
      </upgradeFlowContext.Provider>
    </OnboardingLayout>
  );
};

export default WithAnonymousUser(CustomiseCardPage);
